import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Alert,
  InputAdornment,
  Paper,
  Chip,
  Avatar,
  ListItemAvatar,
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Phone as PhoneIcon,
  LocalPharmacy as PharmacyIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useDebounce } from 'use-debounce';
import patientService, { 
  Patient, 
  PatientMedication, 
  PatientAddress 
} from '../../services/patientService';
import { usePharmacyStore } from '../../stores/pharmacyStore';
import { PatientKrollStatusChips } from '../common/PatientKrollStatusChips';

export interface ExtendedPatient extends Patient {
  medications?: PatientMedication[];
  fullAddress?: string;
}

interface PatientSearchModalProps {
  open: boolean;
  onClose: () => void;
  onSelectPatient: (patient: ExtendedPatient) => void;
  title?: string;
  subtitle?: string;
}

export const PatientSearchModal: React.FC<PatientSearchModalProps> = ({
  open,
  onClose,
  onSelectPatient,
  title = 'Search Patient',
  subtitle = 'Search by last name or "Last, First" format',
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { activePharmacy } = usePharmacyStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [loadingPatientId, setLoadingPatientId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [searchTime, setSearchTime] = useState(0);
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);

  // AbortController ref for cancelling search requests
  const abortControllerRef = useRef<AbortController | null>(null);

  // Perform MQTT-based patient search
  const performSearch = useCallback(async () => {
    // Need at least 2 characters to search
    if (!debouncedSearchTerm || debouncedSearchTerm.trim().length < 2) {
      setPatients([]);
      setTotalResults(0);
      return;
    }

    if (!activePharmacy) {
      setError('Please select a pharmacy first');
      enqueueSnackbar('Please select a pharmacy before searching', { variant: 'warning' });
      return;
    }

    // Cancel previous request if exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setLoading(true);
    setError(null);

    try {
      // Parse search term - support "Last, First" format
      const raw = debouncedSearchTerm.trim();
      let parsedLastName = raw;
      let parsedFirstName = '';
      
      if (raw.includes(',')) {
        const parts = raw.split(',');
        parsedLastName = parts[0].trim();
        parsedFirstName = parts.slice(1).join(',').trim();
      } else if (raw.includes(' ')) {
        const parts = raw.split(/\s+/);
        parsedLastName = parts[0];
        parsedFirstName = parts.slice(1).join(' ');
      }

      const result = await patientService.searchPatientsWithMQTT({
        lastName: parsedLastName,
        firstName: parsedFirstName,
        searchType: 'name',
        limit: 20,
        pharmacyId: activePharmacy.id,
      }, abortController.signal);

      // Extract patients from the pharmacy-specific results
      if (result.pharmacyResults.length > 0) {
        const pharmacyResult = result.pharmacyResults[0];
        setPatients(pharmacyResult.patients);
        setTotalResults(pharmacyResult.total);
        setSearchTime(pharmacyResult.searchTime);
      } else {
        setPatients([]);
        setTotalResults(0);
      }
    } catch (err: any) {
      // Don't show error if request was aborted
      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
        console.log('Search request was cancelled');
        return;
      }

      setError(err.message || 'Failed to search patients');
      enqueueSnackbar(err.message || 'Failed to search patients', { variant: 'error' });
      setPatients([]);
      setTotalResults(0);
    } finally {
      // Only set loading to false if this wasn't an aborted request
      if (abortController === abortControllerRef.current) {
        setLoading(false);
      }
    }
  }, [debouncedSearchTerm, activePharmacy, enqueueSnackbar]);

  // Search when debounced term changes
  useEffect(() => {
    const hasValidInput = debouncedSearchTerm && debouncedSearchTerm.trim().length >= 2;
    if (hasValidInput) {
      performSearch();
    } else {
      setPatients([]);
      setTotalResults(0);
    }
  }, [debouncedSearchTerm]); // Removed performSearch to prevent dependency loop

  // Handle patient selection - immediately fetch details and navigate
  const handleSelectPatient = async (patient: Patient) => {
    if (!activePharmacy) return;

    setLoadingDetails(true);
    setLoadingPatientId(patient.id);

    try {
      // Extract patient number from composite ID if needed
      // The ID format is either "pharmacyId_patientNumber" or just "patientNumber"
      let patientNumber = patient.patientId || patient.id;
      if (patientNumber.includes('_')) {
        // Extract patient number from composite ID
        patientNumber = patientNumber.split('_')[1];
      }

      // Fetch medications from Kroll
      const medicationsData = await patientService.getPatientMedicationsFromKroll(
        activePharmacy.id,
        patientNumber
      );

      // Get full patient data including addresses
      const fullPatientData = await patientService.getPatientFromMQTT(
        activePharmacy.id,
        patientNumber
      );

      // Format address if available
      let fullAddress = '';
      if (fullPatientData.addresses && fullPatientData.addresses.length > 0) {
        const primaryAddress = fullPatientData.addresses.find(a => a.isPreferred) || fullPatientData.addresses[0];
        fullAddress = `${primaryAddress.street1}${primaryAddress.street2 ? ' ' + primaryAddress.street2 : ''}, ${primaryAddress.city}, ${primaryAddress.state} ${primaryAddress.zipCode}`;
      }

      // Convert Kroll medications to our format
      const medications: PatientMedication[] = medicationsData.medications.map(med => ({
        id: med.rxNumber,
        medicationName: med.drugName,
        strength: med.strength,
        dosageForm: '', // Not provided by Kroll
        sig: med.sig,
        startDate: med.firstFillDate,
        nextFillDate: med.nextFillDate,
        adherenceRate: undefined,
        metadata: med,
      }));

      // Ensure we have the composite ID format for the patient
      const compositeId = patient.id.includes('_')
        ? patient.id
        : `${activePharmacy.id}_${patientNumber}`;

      const extendedPatient: ExtendedPatient = {
        ...patient,
        ...fullPatientData,
        id: compositeId, // Ensure composite ID format
        patientId: compositeId, // Keep both for compatibility
        medications,
        fullAddress,
      };

      // Immediately navigate with the extended patient data
      onSelectPatient(extendedPatient);
      handleClose();
    } catch (error: any) {
      console.error('Failed to fetch patient details:', error);
      // Still navigate even if we couldn't get additional details
      const compositeId = patient.id.includes('_')
        ? patient.id
        : `${activePharmacy.id}_${patient.patientId || patient.id}`;

      onSelectPatient({
        ...patient,
        id: compositeId,
        patientId: compositeId,
      });
      handleClose();
    } finally {
      setLoadingDetails(false);
      setLoadingPatientId(null);
    }
  };

  // Reset modal
  const handleClose = () => {
    // Cancel any pending search request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    setSearchTerm('');
    setPatients([]);
    setLoadingPatientId(null);
    setError(null);
    setTotalResults(0);
    onClose();
  };

  // Format date for display - DD/MM/YYYY (Canadian/International standard)
  const formatDate = (dateString: string): string => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <DialogTitle>
        <Typography variant="h5" component="div">
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
        {activePharmacy && (
          <Chip
            icon={<PharmacyIcon />}
            label={`Active: ${activePharmacy.name}`}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ mt: 1 }}
          />
        )}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 3, mb: 3 }}>
          <TextField
            fullWidth
            variant="outlined"
            label="Search Patient"
            placeholder="Last Name (or 'Last, First')"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            disabled={!activePharmacy}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {loading ? <CircularProgress size={20} /> : <PersonIcon />}
                </InputAdornment>
              ),
            }}
            autoFocus
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {totalResults > 0 && activePharmacy && (
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Found {totalResults} patient{totalResults !== 1 ? 's' : ''} in {activePharmacy.name} • {searchTime}ms
          </Typography>
        )}

        {!loading && patients.length > 0 && (
          <List sx={{ maxHeight: '400px', overflow: 'auto' }}>
            {patients.map((patient) => {
              const isLoadingThis = loadingPatientId === patient.id;
              return (
                <Paper
                  key={patient.id}
                  elevation={isLoadingThis ? 3 : 1}
                  sx={{ mb: 1 }}
                >
                  <ListItemButton
                    selected={isLoadingThis}
                    onClick={() => handleSelectPatient(patient)}
                    disabled={loadingDetails}
                    sx={{ py: 2 }}
                  >
                    <ListItemAvatar>
                      <Avatar>
                        {patient.firstName?.[0]}{patient.lastName?.[0]}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" fontWeight="medium">
                            {patient.lastName}, {patient.firstName}
                          </Typography>
                          {/* GP-1402: patient search no longer hides closed / deceased
                              Kroll files, so this picker has to mark them. */}
                          <PatientKrollStatusChips patient={patient} />
                          {patient.mrn && (
                            <Chip
                              label={`MRN: ${patient.mrn}`}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box component="span" sx={{ mt: 1, display: 'block' }}>
                          <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CalendarIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                              <Typography component="span" variant="body2" color="text.secondary">
                                DOB: {formatDate(patient.dateOfBirth)}
                              </Typography>
                            </Box>
                            {(patient.phone || patient.primaryPhone) && (
                              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PhoneIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                <Typography component="span" variant="body2" color="text.secondary">
                                  {patient.primaryPhone || patient.phone}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          {patient.primaryDiagnosis && (
                            <Chip
                              label={patient.primaryDiagnosis}
                              size="small"
                              sx={{ mr: 1 }}
                            />
                          )}
                          {isLoadingThis && (
                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                              <CircularProgress size={16} />
                              <Typography component="span" variant="caption" color="primary.main" fontWeight="medium">
                                Loading patient details...
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      }
                      secondaryTypographyProps={{ component: 'div' }}
                    />
                  </ListItemButton>
                </Paper>
              );
            })}
          </List>
        )}

        {!loading && searchTerm && patients.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="textSecondary">
              No patients found matching "{searchTerm}"
            </Typography>
          </Box>
        )}

        {!loading && !searchTerm && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <PersonIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Search for a Patient
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Enter at least 2 characters to start searching
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loadingDetails}>
          {loadingDetails ? 'Loading...' : 'Cancel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PatientSearchModal;